import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './SettingsPage.module.css'
import OnboardingWizard from './OnboardingWizard.jsx'
import { useAuth } from '../auth/AuthContext.jsx'
import { useTheme } from '../theme/ThemeContext.jsx'
import { useShowSqlSetting } from '../settings/ShowSqlContext.jsx'

function initials(name) {
  const parts = name.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts[parts.length - 1]?.[0] ?? ''
  return (first + (parts.length > 1 ? last : '')).toUpperCase()
}

function Avatar({ name }) {
  return <div className={styles.avatar}>{initials(name)}</div>
}

function Section({ title, tag, children }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>
        {tag && <span className={styles.layerTag}>{tag}</span>}
        {title}
      </div>
      <div className={styles.fieldList}>{children}</div>
    </div>
  )
}

function Field({ label, value, mono, small, accent, ok, muted }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span
        className={[
          styles.fieldValue,
          mono   ? styles.mono   : '',
          small  ? styles.small  : '',
          accent ? styles.accent : '',
          ok     ? styles.ok     : '',
          muted  ? styles.muted2 : '',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  )
}

const TAB_LABELS = { overview: 'Overview', profile: 'Business Profile', users: 'Users', billing: 'Billing', usage: 'Usage', security: 'Security', appearance: 'Appearance' }

export default function SettingsPage() {
  const { apiFetch } = useAuth()
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('tab') || 'overview'
  })
  const [tenantProfile, setTenantProfile] = useState(null)
  const [showWizard, setShowWizard] = useState(false)

  useEffect(() => {
    apiFetch('/api/tenant/profile')
      .then(r => r.json())
      .then(setTenantProfile)
      .catch(() => {})
  }, [apiFetch])

  return (
    <div className={`${styles.root} anim-fade`}>
      {showWizard && (
        <OnboardingWizard onComplete={saved => { setTenantProfile(saved); setShowWizard(false) }} />
      )}

      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.sub}>Organization, billing, and security configuration</p>
      </div>

      <div className={styles.tabBar}>
        {Object.entries(TAB_LABELS).map(([t, label]) => (
          <button
            key={t}
            className={`${styles.tabBtn} ${tab === t ? styles.tabBtnActive : ''}`}
            onClick={() => setTab(t)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {tab === 'overview'    && <OverviewTab profile={tenantProfile} />}
        {tab === 'profile'     && <BusinessProfileTab profile={tenantProfile} onEdit={() => setShowWizard(true)} onSave={setTenantProfile} />}
        {tab === 'users'       && <UsersTab />}
        {tab === 'billing'     && <BillingTab />}
        {tab === 'usage'       && <UsageTab />}
        {tab === 'security'    && <SecurityTab />}
        {tab === 'appearance'  && <AppearanceTab />}
      </div>
    </div>
  )
}

function BusinessProfileTab({ profile, onEdit, onSave }) {
  const { apiFetch } = useAuth()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  function startEdit() {
    setDraft(profile?.ai_context ?? '')
    setEditing(true)
  }

  async function save() {
    if (!draft.trim()) return
    setSaving(true)
    try {
      const res = await apiFetch('/api/tenant/profile', {
        method: 'PUT',
        body: JSON.stringify({ ...(profile ?? {}), ai_context: draft }),
      })
      const saved = await res.json()
      onSave(saved)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const hasProfile = !!profile?.onboarded_at

  return (
    <div className={styles.profileTab}>
      <div className={styles.profileHeader}>
        <div>
          <div className={styles.profileTitle}>Business Profile</div>
          <div className={styles.profileSub}>
            This context is prepended to every AI conversation so Structura always understands your business.
          </div>
        </div>
        <button className={styles.inviteBtn} onClick={onEdit}>
          {hasProfile ? 'Re-run setup wizard' : 'Run setup wizard'}
        </button>
      </div>

      {hasProfile ? (
        <div className={styles.profileCard}>
          <div className={styles.profileCardHeader}>
            <span className={styles.layerTag}>AI CONTEXT</span>
            {!editing && (
              <button className={styles.govBtn} onClick={startEdit}>Edit</button>
            )}
          </div>

          {editing ? (
            <div className={styles.profileEditBlock}>
              <textarea
                className={styles.profileTextarea}
                rows={5}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                autoFocus
              />
              <div className={styles.profileEditActions}>
                <button className={styles.govBtn} onClick={() => setEditing(false)}>Cancel</button>
                <button className={styles.inviteBtn} onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <p className={styles.profileContext}>{profile.ai_context}</p>
          )}

          <div className={styles.profileMeta}>
            {profile.company_name && <span className={styles.profileMetaItem}>{profile.company_name}</span>}
            {profile.industry     && <span className={styles.profileMetaItem}>{profile.industry}</span>}
            {profile.onboarded_at && (
              <span className={styles.profileMetaItem}>
                Set up {new Date(profile.onboarded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>

          {profile.entities?.length > 0 && (
            <div className={styles.profileChips}>
              <span className={styles.profileChipLabel}>Entities</span>
              {profile.entities.map(e => (
                <span key={e} className={styles.profileChip}>{e.replace(/_/g, ' ')}</span>
              ))}
            </div>
          )}

          {profile.kpis?.length > 0 && (
            <div className={styles.profileChips}>
              <span className={styles.profileChipLabel}>KPIs</span>
              {profile.kpis.map(k => (
                <span key={k} className={`${styles.profileChip} ${styles.profileChipTeal}`}>{k}</span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.profileEmpty}>
          <div className={styles.profileEmptyIcon}>◈</div>
          <div className={styles.profileEmptyTitle}>No business profile yet</div>
          <div className={styles.profileEmptySub}>
            Run the setup wizard to give the AI context about your business, KPIs, and what data you track.
          </div>
          <button className={styles.inviteBtn} onClick={onEdit}>Run setup wizard →</button>
        </div>
      )}
    </div>
  )
}

function OverviewTab({ profile }) {
  return (
    <div className={styles.grid}>
      <Section title="ORGANIZATION" tag="ORG">
        <Field label="Organization Name" value={profile?.company_name || '—'} />
        <Field label="Tenant ID"         value={profile?.tenant_id || '—'} mono small />
        <Field label="Industry"          value={profile?.industry || '—'} />
      </Section>

      <Section title="DATA LAKE">
        <Field label="Storage Location" value={profile?.tenant_id ? `structuradata-${profile.tenant_id}` : '—'} mono small />
        <Field label="Region"       value="us-west-2" mono />
        <Field label="Encryption"   value="Encrypted at rest (AWS KMS)" ok />
      </Section>
    </div>
  )
}

function UsersTab() {
  const { apiFetch, user: currentUser } = useAuth()
  const isAdmin = currentUser?.groups?.includes('Admin') || currentUser?.role === 'Admin'
  const [users, setUsers]         = useState([])
  const [loading, setLoading]     = useState(true)
  const [inviteEmail, setEmail]   = useState('')
  const [inviteRole, setRole]     = useState('Analyst')
  const [inviteError, setInviteErr] = useState('')
  const [inviting, setInviting]   = useState(false)

  useEffect(() => {
    apiFetch('/api/users')
      .then(r => r.json())
      .then(data => { setUsers(Array.isArray(data) ? data : []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [apiFetch])

  async function sendInvite() {
    if (!inviteEmail.trim()) { setInviteErr('Email is required'); return }
    setInviteErr('')
    setInviting(true)
    try {
      const res = await apiFetch('/api/users/invite', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      })
      const newUser = await res.json()
      if (!res.ok) throw new Error(newUser.error || 'Failed to invite')
      setUsers(prev => [...prev, newUser])
      setEmail('')
    } catch (err) {
      setInviteErr(err.message || 'Failed to send invite')
    } finally {
      setInviting(false)
    }
  }

  async function toggleStatus(sub, currentStatus) {
    const enable = currentStatus !== 'active'
    setUsers(prev => prev.map(u => u.sub === sub ? { ...u, status: enable ? 'active' : 'deactivated' } : u))
    try {
      await apiFetch(`/api/users/${sub}/status`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: enable }),
      })
    } catch (err) {
      console.error('Toggle status error:', err)
      setUsers(prev => prev.map(u => u.sub === sub ? { ...u, status: currentStatus } : u))
    }
  }

  async function changeRole(sub, role) {
    setUsers(prev => prev.map(u => u.sub === sub ? { ...u, role } : u))
    try {
      await apiFetch(`/api/users/${sub}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role }),
      })
    } catch (err) {
      console.error('Change role error:', err)
    }
  }

  async function deleteUser(sub, email) {
    if (!window.confirm(`Remove ${email} from this workspace?`)) return
    try {
      await apiFetch(`/api/users/${sub}`, { method: 'DELETE' })
      setUsers(prev => prev.filter(u => u.sub !== sub))
    } catch (err) {
      console.error('Delete user error:', err)
    }
  }

  return (
    <div>
      {isAdmin && (
        <div>
          <div className={`${styles.inviteBar} ${inviteError ? styles.inviteBarError : ''}`}>
            <input
              className={`${styles.inviteInput} ${inviteError ? styles.inputError : ''}`}
              type="email"
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={e => { setEmail(e.target.value); if (inviteError) setInviteErr('') }}
              onKeyDown={e => e.key === 'Enter' && sendInvite()}
            />
            <select
              className={styles.inviteSelect}
              value={inviteRole}
              onChange={e => setRole(e.target.value)}
            >
              <option>Admin</option>
              <option>Analyst</option>
              <option>Viewer</option>
            </select>
            <button className={styles.inviteBtn} onClick={sendInvite} disabled={inviting}>
              {inviting ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
          {inviteError && <div className={styles.inviteErrorMsg}>{inviteError}</div>}
        </div>
      )}

      {loading ? (
        <div className={styles.emptyState}>Loading users…</div>
      ) : users.length === 0 ? (
        <div className={styles.emptyState}>No users found. Invite a teammate above.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>User</th>
              <th className={styles.th}>Role</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Member Since</th>
              {isAdmin && <th className={styles.th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.sub} className={styles.tr}>
                <td className={styles.td}>
                  <div className={styles.userCell}>
                    <Avatar name={u.name} />
                    <div>
                      <div className={styles.userName}>{u.name}</div>
                      <div className={styles.userEmail}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className={styles.td}>
                  <select
                    className={styles.roleSelect}
                    value={u.role}
                    onChange={e => changeRole(u.sub, e.target.value)}
                  >
                    <option>Admin</option>
                    <option>Analyst</option>
                    <option>Viewer</option>
                  </select>
                </td>
                <td className={styles.td}>
                  <StatusBadge status={u.status} />
                </td>
                <td className={styles.td}>
                  <span className={styles.lastActive}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </span>
                </td>
                {isAdmin && (
                  <td className={styles.td}>
                    <div className={styles.actionBtns}>
                      {u.status !== 'invited' && (
                        <button
                          className={u.status === 'active' ? styles.deactivateBtn : styles.activateBtn}
                          onClick={() => toggleStatus(u.sub, u.status)}
                        >
                          {u.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      {u.sub !== currentUser?.sub && (
                        <button
                          className={styles.deleteUserBtn}
                          onClick={() => deleteUser(u.sub, u.email)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function StatusBadge({ status }) {
  const cls = {
    active:      styles.badgeActive,
    invited:     styles.badgeInvited,
    deactivated: styles.badgeDeactivated,
  }[status] ?? styles.badgeDeactivated

  const label = status.charAt(0).toUpperCase() + status.slice(1)
  return <span className={`${styles.badge} ${cls}`}>{label}</span>
}

function UsageTab() {
  const { apiFetch } = useAuth()
  const [usage, setUsage] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiFetch('/api/usage')
      .then(r => r.json())
      .then(d => { setUsage(d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [apiFetch])

  useEffect(() => {
    apiFetch('/api/users')
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [apiFetch])

  if (loading) return <div className={styles.emptyState}>Loading usage…</div>
  if (!usage)  return <div className={styles.emptyState}>Unable to load usage data.</div>

  const pct     = Math.min((usage.used / usage.limit) * 100, 100)
  const prevPct = Math.min((usage.prev_used / usage.limit) * 100, 100)
  const warn    = pct >= 80
  const crit    = pct >= 95

  function fmtTokens(n) {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`
    return String(n)
  }

  function fmtMonth(m) {
    const [y, mo] = m.split('-')
    return new Date(Number(y), Number(mo) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const byUser = Object.entries(usage.by_user ?? {}).sort((a, b) => b[1] - a[1])

  return (
    <div className={styles.usageTab}>
      <Section title="AI TOKEN USAGE" tag="USAGE">
        <div className={styles.usageCard}>
          <div className={styles.usageHeader}>
            <span className={styles.usageMonth}>{fmtMonth(usage.month)}</span>
            <span className={crit ? styles.usageCrit : warn ? styles.usageWarn : styles.usageOk}>
              {fmtTokens(usage.used)} / {fmtTokens(usage.limit)} tokens
            </span>
          </div>
          <div className={styles.usageBarTrack}>
            <div
              className={`${styles.usageBarFill} ${crit ? styles.usageBarCrit : warn ? styles.usageBarWarn : ''}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className={styles.usageMeta}>
            <span className={styles.usageMetaText}>{pct.toFixed(1)}% of monthly budget used</span>
            {crit && <span className={styles.usageCritBadge}>Near limit</span>}
            {warn && !crit && <span className={styles.usageWarnBadge}>High usage</span>}
          </div>
        </div>

        {usage.prev_used > 0 && (
          <div className={styles.usagePrev}>
            <span className={styles.usagePrevLabel}>{fmtMonth(usage.prev_month)}</span>
            <div className={styles.usageBarTrack} style={{ marginTop: 6 }}>
              <div className={styles.usageBarFill} style={{ width: `${prevPct}%`, opacity: 0.4 }} />
            </div>
            <span className={styles.usageMetaText}>{fmtTokens(usage.prev_used)} tokens used</span>
          </div>
        )}
      </Section>

      <Section title="BUDGET SETTINGS" tag="LIMIT">
        <Field label="Monthly Token Limit" value={`${fmtTokens(usage.limit)} tokens`} />
        <Field label="Overage Policy" value="Conversations paused at limit" muted />
      </Section>

      {byUser.length > 0 && (
        <Section title="USAGE BY TEAM MEMBER" tag="USERS">
          {byUser.map(([sub, tokens]) => {
            const u = users.find(x => x.sub === sub)
            return <Field key={sub} label={u?.name || u?.email || sub} value={`${fmtTokens(tokens)} tokens`} mono={!u} />
          })}
        </Section>
      )}
    </div>
  )
}

function DeleteOrgModal({ onClose }) {
  const [confirm, setConfirm] = useState('')
  const orgName = 'DELETE'

  return (
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div className={styles.modalBox} onMouseDown={e => e.stopPropagation()}>
        <div className={styles.modalTitle}>Delete Organization</div>
        <p className={styles.modalBody}>
          This will permanently delete your organization, all data lake files, and every user account.
          This action <strong>cannot be undone</strong>.
        </p>
        <p className={styles.modalBody}>
          Type <strong>{orgName}</strong> to confirm:
        </p>
        <input
          className={styles.modalInput}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder={orgName}
          autoFocus
        />
        <div className={styles.modalActions}>
          <button className={styles.modalCancel} onClick={onClose}>Cancel</button>
          <button
            className={styles.modalDelete}
            disabled={confirm !== orgName}
          >
            Delete Organization
          </button>
        </div>
      </div>
    </div>
  )
}

function SecurityTab() {
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  return (
    <div className={styles.securityRoot}>
      {showDeleteModal && <DeleteOrgModal onClose={() => setShowDeleteModal(false)} />}

      <div className={styles.grid}>
        <Section title="AI & MODEL">
          <Field label="LLM Provider" value="Amazon Bedrock" />
          <Field label="Model"        value="Claude Sonnet (managed)" />
        </Section>

        <Section title="ACCESS CONTROL" tag="IAM">
          <div className={styles.field}>
            <span className={styles.fieldLabel}>SSO</span>
            <span className={styles.fieldValue}>
              Disabled{' '}
              <span className={styles.enterpriseBadge}>Upgrade to Enterprise</span>
            </span>
          </div>
          <Field label="IP Allowlist" value="Not configured" muted />
        </Section>
      </div>

      <div className={styles.section} style={{ marginTop: 'var(--sp-6)' }}>
        <div className={styles.sectionLabel}>
          <span className={styles.layerTag}>GOV</span>
          DATA GOVERNANCE
        </div>
        <div className={styles.fieldList}>
          <div className={styles.govRow}>
            <span className={styles.govLabel}>Row-Level Security</span>
            <span className={styles.govValue}>Not configured</span>
            <span />
          </div>
          <div className={styles.govRow}>
            <span className={styles.govLabel}>Column PII Tagging</span>
            <span className={styles.govValue}>Managed in Catalog</span>
            <button className={styles.govBtn} onClick={() => navigate('/lake')}>Go to Catalog →</button>
          </div>
          <div className={`${styles.govRow} ${styles.govRowLast}`}>
            <span className={styles.govLabel}>Audit Log</span>
            <span className={`${styles.govValue} ${styles.ok}`}>CloudTrail enabled</span>
            <span />
          </div>
        </div>
      </div>

      <div className={`${styles.dangerZone} anim-fade-3`} style={{ marginTop: 'var(--sp-8)' }}>
        <div className={styles.dangerLabel}>Danger Zone</div>
        <div className={styles.dangerRow}>
          <div>
            <div className={styles.dangerAction}>Delete Organization</div>
            <div className={styles.dangerDesc}>Permanently delete this org and all lake data. Irreversible.</div>
          </div>
          <button className={styles.dangerBtn} onClick={() => setShowDeleteModal(true)}>Delete…</button>
        </div>
      </div>
    </div>
  )
}

function AppearanceTab() {
  const { theme, setTheme } = useTheme()
  const { showSqlEnabled, setShowSqlEnabled } = useShowSqlSetting()
  return (
    <div>
      <Section title="DISPLAY PREFERENCES" tag="UI">
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Color Theme</span>
          <div className={styles.themeToggle}>
            {['dark', 'light'].map(t => (
              <button
                key={t}
                className={`${styles.themeOption} ${theme === t ? styles.themeOptionActive : ''}`}
                onClick={() => setTheme(t)}
              >
                <span className={styles.themeSwatch} data-swatch={t} />
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Show SQL in AI Responses</span>
          <div className={styles.themeToggle}>
            {[true, false].map(v => (
              <button
                key={String(v)}
                className={`${styles.themeOption} ${showSqlEnabled === v ? styles.themeOptionActive : ''}`}
                onClick={() => setShowSqlEnabled(v)}
              >
                {v ? 'On' : 'Off'}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Preference</span>
          <span className={styles.fieldValue} style={{ fontSize: 12, color: 'var(--color-text-faint)' }}>
            Saved locally to this browser. Sync across devices coming soon.
          </span>
        </div>
      </Section>
    </div>
  )
}

function BillingTab() {
  const { apiFetch } = useAuth()
  const [sub, setSub]         = useState(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [err, setErr]         = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') === 'success') {
      window.history.replaceState({}, '', '/settings?tab=billing')
    }
    apiFetch('/api/billing/subscription')
      .then(r => r.json())
      .then(d => { setSub(d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [apiFetch])

  async function startCheckout() {
    setWorking(true); setErr(null)
    try {
      const res = await apiFetch('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ origin: window.location.origin }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed')
      window.location.href = data.url
    } catch (e) {
      setErr(e.message)
      setWorking(false)
    }
  }

  async function openPortal() {
    setWorking(true); setErr(null)
    try {
      const res = await apiFetch('/api/billing/portal', {
        method: 'POST',
        body: JSON.stringify({ origin: window.location.origin }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed')
      window.location.href = data.url
    } catch (e) {
      setErr(e.message)
      setWorking(false)
    }
  }

  if (loading) return <div className={styles.emptyState}>Loading billing…</div>

  const isActive   = sub?.subscription_status === 'active' || sub?.subscription_status === 'trialing'
  const isCanceled = sub?.subscription_status === 'canceled'
  const periodEnd  = sub?.current_period_end
    ? new Date(sub.current_period_end * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null

  return (
    <div className={styles.billingTab}>
      <Section title="SUBSCRIPTION" tag="BILLING">
        {isActive ? (
          <>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Plan</span>
              <span className={`${styles.fieldValue} ${styles.ok}`}>{sub.plan_name ?? 'Pro'}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Status</span>
              <span className={`${styles.fieldValue} ${styles.ok}`}>{sub.subscription_status.charAt(0).toUpperCase() + sub.subscription_status.slice(1)}</span>
            </div>
            {periodEnd && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Next billing date</span>
                <span className={styles.fieldValue}>{periodEnd}</span>
              </div>
            )}
          </>
        ) : isCanceled ? (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Status</span>
            <span className={`${styles.fieldValue} ${styles.accent}`}>Canceled{periodEnd ? ` — access until ${periodEnd}` : ''}</span>
          </div>
        ) : (
          <div className={styles.billingNoPlan}>
            <div className={styles.billingNoPlanTitle}>No active subscription</div>
            <div className={styles.billingNoPlanSub}>
              Upgrade to unlock unlimited AI queries, automated pipelines, and priority support.
            </div>
          </div>
        )}
      </Section>

      <div className={styles.billingActions}>
        {isActive || isCanceled ? (
          <button className={styles.inviteBtn} onClick={openPortal} disabled={working}>
            {working ? 'Redirecting…' : 'Manage Billing →'}
          </button>
        ) : (
          <button className={styles.inviteBtn} onClick={startCheckout} disabled={working}>
            {working ? 'Redirecting…' : 'Upgrade to Pro →'}
          </button>
        )}
        {err && <span className={styles.limitErr}>{err}</span>}
      </div>

      <Section title="FEATURES" tag="PLAN">
        <div className={styles.field}><span className={styles.fieldLabel}>AI Analyst</span><span className={`${styles.fieldValue} ${styles.ok}`}>Included</span></div>
        <div className={styles.field}><span className={styles.fieldLabel}>Data Connectors</span><span className={`${styles.fieldValue} ${styles.ok}`}>Included</span></div>
        <div className={styles.field}><span className={styles.fieldLabel}>dbt Pipeline</span><span className={`${styles.fieldValue} ${styles.ok}`}>Included</span></div>
        <div className={styles.field}><span className={styles.fieldLabel}>SSO / SAML</span><span className={styles.fieldValue}><span className={styles.enterpriseBadge}>Enterprise</span></span></div>
      </Section>
    </div>
  )
}

